'use client'

import React, { useState, useEffect } from 'react'
import styles from './TerminalTabs.module.css'
import { ConnectorManager } from '../../ConnectorManager'
import { usePathname, useSearchParams } from 'next/navigation'

interface OpenOrder {
  symbol: string
  orderId: number
  orderListId: number
  clientOrderId: string
  price: string
  origQty: string
  executedQty: string
  cummulativeQuoteQty: string
  status: string
  timeInForce: string
  type: string
  side: string
  stopPrice: string
  icebergQty: string
  time: number
  updateTime: number
  isWorking: boolean
  origQuoteOrderQty: string
}

// Interface to group orders
interface OrderGroup {
  mainOrder: OpenOrder
  takeProfit?: OpenOrder
  stopLoss?: OpenOrder
  expanded: boolean
}

export default function OpenOrders() {
  const searchParams = useSearchParams()

  // Get symbol from URL path instead of query parameters
  const pathname = usePathname()
  const pathSymbol = pathname ? pathname.split('/').pop()?.toUpperCase() : ''

  const urlSymbol = pathSymbol || searchParams?.get('symbol')

  const [openOrders, setOpenOrders] = useState<OpenOrder[]>([])
  const [allOpenOrders, setAllOpenOrders] = useState<OpenOrder[]>([]) // Store all orders
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedSymbol, setSelectedSymbol] = useState('ALL')
  const [hideOtherPairs, setHideOtherPairs] = useState(false)
  const [availableSymbols, setAvailableSymbols] = useState<string[]>([])
  const [expandedGroups, setExpandedGroups] = useState<{
    [key: number]: boolean
  }>({})
  const connectorManager = ConnectorManager.getInstance()

  useEffect(() => {
    fetchAllOpenOrders()
  }, [])

  useEffect(() => {
    if (allOpenOrders.length > 0) {
      filterOrders()
    }
  }, [selectedSymbol, hideOtherPairs, allOpenOrders, urlSymbol])

  const fetchAllOpenOrders = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const binanceConnector = connectorManager.getConnector('binance')

      if (!binanceConnector) {
        throw new Error('NINX connector not found or not connected')
      }

      // Fetch all open orders
      const orders = await binanceConnector.getOpenOrders()
      setAllOpenOrders(orders)

      // Extract unique symbols from orders
      const symbols = ['ALL', ...new Set(orders.map(order => order.symbol))]
      setAvailableSymbols(symbols)

      // Filter orders based on selected symbol and hideOtherPairs
      filterOrders()
    } catch (err: any) {
      console.error('Error fetching open orders:', err)
      setError(
        err.message ||
          'Failed to fetch open orders. Please ensure you are connected to Binance.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const filterOrders = () => {
    // If hideOtherPairs is checked and we have a URL symbol, only show that symbol
    if (hideOtherPairs && urlSymbol) {
      setOpenOrders(allOpenOrders.filter(order => order.symbol === urlSymbol))
      return
    }

    // Otherwise filter by the dropdown selection
    if (selectedSymbol === 'ALL') {
      setOpenOrders(allOpenOrders)
    } else {
      setOpenOrders(
        allOpenOrders.filter(order => order.symbol === selectedSymbol)
      )
    }
  }

  const handleSymbolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSymbol(e.target.value)
  }

  const handleHideOtherPairsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setHideOtherPairs(e.target.checked)
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString()
  }

  // Group orders by orderListId
  const groupOrdersByOrderListId = (orders: OpenOrder[]): OrderGroup[] => {
    const orderGroups: { [key: number]: OpenOrder[] } = {}

    // Group orders by orderListId
    orders.forEach(order => {
      if (order.orderListId > 0) {
        if (!orderGroups[order.orderListId]) {
          orderGroups[order.orderListId] = []
        }
        orderGroups[order.orderListId].push(order)
      }
    })

    // Create order groups with main order, TP, and SL
    const result: OrderGroup[] = []

    // First add all orders that are not part of a group
    orders.forEach(order => {
      if (order.orderListId === 0 || order.orderListId === -1) {
        result.push({
          mainOrder: order,
          expanded: !!expandedGroups[order.orderId]
        })
      }
    })

    // Then add grouped orders
    Object.entries(orderGroups).forEach(([orderListId, groupOrders]) => {
      if (groupOrders.length > 1) {
        // Find the main order (usually LIMIT), TP (usually LIMIT_MAKER), and SL (usually STOP_LOSS or STOP_LOSS_LIMIT)
        const mainOrder =
          groupOrders.find(o => o.type === 'LIMIT') || groupOrders[0]
        const takeProfit = groupOrders.find(o => o.type === 'LIMIT_MAKER')
        const stopLoss = groupOrders.find(
          o =>
            o.type === 'STOP_LOSS' ||
            o.type === 'STOP_LOSS_LIMIT' ||
            o.type === 'STOP_MARKET'
        )

        result.push({
          mainOrder,
          takeProfit,
          stopLoss,
          expanded: !!expandedGroups[parseInt(orderListId)]
        })
      } else if (groupOrders.length === 1) {
        // If there's only one order in the group, add it as a single order
        result.push({
          mainOrder: groupOrders[0],
          expanded: !!expandedGroups[groupOrders[0].orderId]
        })
      }
    })

    return result
  }

  // Toggle expanded state
  const toggleExpanded = (id: number) => {
    setExpandedGroups(prev => ({
      ...prev,
      [id]: !prev[id]
    }))
  }

  if (isLoading)
    return <div className={styles.loadingState}>Loading open orders...</div>
  if (error) return <div className={styles.errorState}>{error}</div>

  return (
    <div className={styles.ordersContainer}>
      <div className={styles.symbolSelector}>
        <div className={styles.selectorWrapper}>
          <select
            value={selectedSymbol}
            onChange={handleSymbolChange}
            className={styles.symbolDropdown}
            disabled={hideOtherPairs && urlSymbol ? true : false}
          >
            {availableSymbols.map(symbol => (
              <option key={symbol} value={symbol}>
                {symbol === 'ALL' ? 'All Symbols' : symbol}
              </option>
            ))}
          </select>
        </div>

        {urlSymbol && (
          <div className={styles.filterOption}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={hideOtherPairs}
                onChange={handleHideOtherPairsChange}
                className={styles.checkbox}
              />
              Hide other pairs
            </label>
          </div>
        )}
      </div>

      {openOrders.length === 0 ? (
        <div className={styles.emptyState}>
          {hideOtherPairs && urlSymbol
            ? `No open orders found for ${urlSymbol}`
            : selectedSymbol === 'ALL'
              ? 'No open orders found'
              : `No open orders found for ${selectedSymbol}`}
        </div>
      ) : (
        <div className={styles.ordersTable}>
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Date</th>
                <th>Symbol</th>
                <th>Type</th>
                <th>Side</th>
                <th>Price</th>
                <th>Stop Price</th>
                <th>Amount</th>
                <th>Filled</th>
                <th>Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {groupOrdersByOrderListId(openOrders).map(group => {
                const { mainOrder, takeProfit, stopLoss, expanded } = group
                const hasRelatedOrders = takeProfit || stopLoss

                const price = parseFloat(mainOrder.price)
                const amount = parseFloat(mainOrder.origQty)
                const filled = parseFloat(mainOrder.executedQty)
                const total = price * amount
                const stopPrice = parseFloat(mainOrder.stopPrice)

                return (
                  <React.Fragment key={mainOrder.orderId}>
                    <tr className={hasRelatedOrders ? styles.groupMainRow : ''}>
                      <td>
                        {hasRelatedOrders && (
                          <button
                            className={styles.expandButton}
                            onClick={() =>
                              toggleExpanded(mainOrder.orderListId)
                            }
                          >
                            {expanded ? '▼' : '►'}
                          </button>
                        )}
                      </td>
                      <td>{formatDate(mainOrder.time)}</td>
                      <td>{mainOrder.symbol}</td>
                      <td>{mainOrder.type}</td>
                      <td
                        className={
                          mainOrder.side === 'BUY'
                            ? styles.buyOrder
                            : styles.sellOrder
                        }
                      >
                        {mainOrder.side}
                      </td>
                      <td>${price.toFixed(price < 10.0 ? 8 : 2)}</td>
                      <td>
                        {stopPrice > 0
                          ? `$${stopPrice.toFixed(stopPrice < 10.0 ? 8 : 2)}`
                          : '-'}
                      </td>
                      <td>{amount.toFixed(8)}</td>
                      <td>{filled.toFixed(8)}</td>
                      <td>${total.toFixed(2)}</td>
                      <td>{mainOrder.status}</td>
                    </tr>

                    {hasRelatedOrders && expanded && (
                      <>
                        <tr className={styles.subRow}>
                          <td></td>
                          <td colSpan={10} className={styles.subRowInfo}>
                            <div className={styles.orderDescription}>
                              <strong>A (Limit Order):</strong> If Order A is
                              filled fully, orders B and C will be placed
                            </div>
                          </td>
                        </tr>

                        {takeProfit && (
                          <tr className={styles.subRow}>
                            <td></td>
                            <td colSpan={10} className={styles.subRowInfo}>
                              <div className={styles.orderDescription}>
                                <strong>B (Take Profit):</strong> If order B is
                                triggered, order C will be cancelled
                              </div>
                              <div className={styles.orderDetails}>
                                Type: {takeProfit.type}, Side:{' '}
                                <span
                                  className={
                                    takeProfit.side === 'BUY'
                                      ? styles.buyOrder
                                      : styles.sellOrder
                                  }
                                >
                                  {takeProfit.side}
                                </span>
                                , Price: $
                                {parseFloat(takeProfit.price).toFixed(2)}
                              </div>
                            </td>
                          </tr>
                        )}

                        {stopLoss && (
                          <tr className={styles.subRow}>
                            <td></td>
                            <td colSpan={10} className={styles.subRowInfo}>
                              <div className={styles.orderDescription}>
                                <strong>C (Stop Loss):</strong> If order C is
                                triggered, order B will be cancelled
                              </div>
                              <div className={styles.orderDetails}>
                                Type: {stopLoss.type}, Side:{' '}
                                <span
                                  className={
                                    stopLoss.side === 'BUY'
                                      ? styles.buyOrder
                                      : styles.sellOrder
                                  }
                                >
                                  {stopLoss.side}
                                </span>
                                , Stop Price: $
                                {parseFloat(stopLoss.stopPrice).toFixed(2)}
                                {stopLoss.type === 'STOP_LOSS_LIMIT' &&
                                  parseFloat(stopLoss.price) > 0 && (
                                    <>
                                      , Limit Price: $
                                      {parseFloat(stopLoss.price).toFixed(2)}
                                    </>
                                  )}
                                {stopLoss.type === 'STOP_LOSS' &&
                                  parseFloat(stopLoss.price) === 0 && (
                                    <> (Market Order)</>
                                  )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
